import { useState } from 'react';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { LoadingState, EmptyState, ErrorState } from '@/components/ui/States';
import { useAuth } from '@/hooks/useAuth';
import { useLabOrders, useLabDepartments } from './hooks';
import { LabStatusBadge, PriorityBadge, fmtDateTime } from './ui';
import NewLabOrderDialog from './NewLabOrderDialog';
import LabOrderDetails from './LabOrderDetails';
import type { LabStatus } from './types';

/**
 * The order work queue.
 *
 * Filtering, sorting and paging are all SERVER-side (see
 * routes/laboratory/orders.js). Filtering a fetched page in the browser would
 * silently only search the rows that happened to be on screen — on a list
 * this size that reads as "the order isn't there", which is the worst
 * possible answer for someone holding a requisition.
 */

/** The named work queues, in workflow order. */
const QUEUES: Array<{ id: string; label: string; status?: string }> = [
  { id: 'all',       label: 'All orders' },
  { id: 'new',       label: 'New',                  status: 'ORDERED,PENDING_COLLECTION' },
  { id: 'collect',   label: 'Pending collection',   status: 'PENDING_COLLECTION' },
  { id: 'process',   label: 'Processing',           status: 'COLLECTED,RECEIVED,ACCEPTED,PROCESSING' },
  { id: 'result',    label: 'Result pending',       status: 'ACCEPTED,PROCESSING' },
  { id: 'verify',    label: 'Verification pending', status: 'RESULT_ENTERED' },
  { id: 'report',    label: 'Report pending',       status: 'VERIFIED' },
  { id: 'done',      label: 'Completed',            status: 'REPORTED' },
];

export interface LabOrdersTabProps {
  /** Filters handed over from a dashboard tile click. */
  initialFilter?: Record<string, string>;
}

export default function LabOrdersTab({ initialFilter }: LabOrdersTabProps) {
  const { user } = useAuth();
  const [queue, setQueue] = useState(() => {
    if (!initialFilter?.status) return 'all';
    return QUEUES.find((q) => q.status === initialFilter.status)?.id ?? 'all';
  });
  const [q, setQ] = useState('');
  const [priority, setPriority] = useState(initialFilter?.priority ?? '');
  const [departmentId, setDepartmentId] = useState<number | ''>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [newOpen, setNewOpen] = useState(false);
  const [openOrderId, setOpenOrderId] = useState<number | null>(null);

  const departments = useLabDepartments();
  const activeQueue = QUEUES.find((x) => x.id === queue);

  const { data, isLoading, error, refetch, isFetching } = useLabOrders({
    status: activeQueue?.status ?? initialFilter?.status,
    priority: priority || undefined,
    department_id: departmentId || undefined,
    q: q.trim() || undefined,
    from: from || undefined,
    to: to || undefined,
    page,
  });

  const canOrder = !!user?.modules?.includes('lab:orders');
  const rows = data?.data ?? [];

  const setQueueAndReset = (id: string) => { setQueue(id); setPage(1); };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {QUEUES.map((qq) => (
          <button
            key={qq.id}
            type="button"
            onClick={() => setQueueAndReset(qq.id)}
            className={`rounded-full px-3 py-1 text-[12px] ${
              queue === qq.id ? 'bg-teal text-white font-semibold' : 'border border-border hover:bg-muted'
            }`}
          >
            {qq.label}
          </button>
        ))}
        <div className="ml-auto">
          {canOrder && <Button size="sm" onClick={() => setNewOpen(true)}>+ New order</Button>}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Input
          className="min-w-[220px] flex-1"
          placeholder="Patient, UHID or order number…"
          value={q}
          onChange={(e) => { setQ(e.target.value); setPage(1); }}
        />
        <select className="h-9 rounded-lg border border-border bg-card px-2 text-[13px]"
          value={priority} onChange={(e) => { setPriority(e.target.value); setPage(1); }}>
          <option value="">Any priority</option>
          <option value="stat">STAT</option>
          <option value="urgent">Urgent</option>
          <option value="routine">Routine</option>
        </select>
        <select className="h-9 rounded-lg border border-border bg-card px-2 text-[13px]"
          value={departmentId} onChange={(e) => { setDepartmentId(e.target.value ? Number(e.target.value) : ''); setPage(1); }}>
          <option value="">All departments</option>
          {(departments.data ?? []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <Input type="date" className="w-[150px]" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} />
        <Input type="date" className="w-[150px]" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} />
      </div>

      {isLoading && <LoadingState label="Loading orders…" />}
      {error && <ErrorState message={(error as Error).message} onRetry={() => refetch()} />}

      {!isLoading && !error && rows.length === 0 && (
        <EmptyState label="No orders match these filters." hint="Try widening the date range or clearing the queue filter." />
      )}

      {rows.length > 0 && (
        <>
          <div className="rounded-xl border border-border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order</TableHead>
                  <TableHead>Patient</TableHead>
                  <TableHead>Tests</TableHead>
                  <TableHead>Ordered</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((o) => (
                  <TableRow key={o.id} className="cursor-pointer" onClick={() => setOpenOrderId(o.id)}>
                    <TableCell>
                      <div className="font-mono text-[12px]">{o.order_number}</div>
                      <div className="flex gap-1"><PriorityBadge priority={o.priority} /></div>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{o.patient_name}</div>
                      <div className="text-[11px] text-muted-foreground">
                        {o.uhid} · {o.age ?? '—'}{o.age ? 'y' : ''}, {o.gender}
                        {o.visit_code ? ` · ${o.visit_code}` : ''}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="tabular-nums">{o.test_count}</span>
                      {Number(o.verified_count) > 0 && (
                        <span className="text-[11px] text-muted-foreground"> · {o.verified_count} verified</span>
                      )}
                      {o.has_critical && <Badge variant="danger" className="ml-1.5">Critical</Badge>}
                    </TableCell>
                    <TableCell className="text-[12px] text-muted-foreground">{fmtDateTime(o.ordered_at)}</TableCell>
                    <TableCell><LabStatusBadge status={o.status as LabStatus} /></TableCell>
                    <TableCell className="text-right">
                      <Button variant="link" size="sm" onClick={(e) => { e.stopPropagation(); setOpenOrderId(o.id); }}>
                        Open
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between text-[12px] text-muted-foreground">
            <span>
              {data?.total !== null && data?.total !== undefined
                ? `${data.total} order${data.total === 1 ? '' : 's'}`
                : `${rows.length} shown`}
              {isFetching && ' · refreshing…'}
            </span>
            <span className="flex items-center gap-2">
              <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
              <span>Page {page}</span>
              <Button variant="ghost" size="sm" disabled={!data?.has_more} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </span>
          </div>
        </>
      )}

      <NewLabOrderDialog
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onCreated={(id) => setOpenOrderId(id)}
      />
      <LabOrderDetails orderId={openOrderId} onClose={() => setOpenOrderId(null)} />
    </div>
  );
}
